// hooks/useFilterPersistence.ts
import { CertificateTypeFilterValue } from '@ui-src/features/certificates-overview';
import { OrderStatus } from '@ui-src/features/certificates-overview/utils/certificatesStatus';
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'certificateOverviewFilters';

export const clearFilters = () => {
  localStorage.removeItem(STORAGE_KEY);
};

interface FilterState {
  search?: string;
  filter: CertificateTypeFilterValue[];
  status?: OrderStatus;
}

export const useCertificateOverviewFilterPersistence = () => {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus>();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [activeTypeFilter, setActiveTypeFilter] = useState<CertificateTypeFilterValue[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load filters from URL or localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('search');
    const filter = params.get('filter');
    const status = params.get('status');

    if (search || filter || status) {
      setSearchQuery(search || undefined);
      setActiveTypeFilter(filter ? (filter.split(',') as CertificateTypeFilterValue[]) : []);
      setSelectedStatus((status as OrderStatus) || undefined);
    } else {
      const savedFilters = localStorage.getItem(STORAGE_KEY);
      if (savedFilters) {
        const { search, filter, status } = JSON.parse(savedFilters) as FilterState;
        setSearchQuery(search);
        setActiveTypeFilter(filter || []);
        setSelectedStatus(status);
      }
    }
    setIsInitialized(true);
  }, []);

  // Persist filters to localStorage and URL
  useEffect(() => {
    if (!isInitialized) return;

    // Save filter state to localStorage
    const filterState: FilterState = {
      search: searchQuery,
      filter: activeTypeFilter,
      status: selectedStatus,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));

    const params = new URLSearchParams(window.location.search);

    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }

    if (Array.isArray(activeTypeFilter) && activeTypeFilter.length > 0) {
      params.set('filter', activeTypeFilter.join(','));
    } else {
      params.delete('filter');
    }

    if (selectedStatus) {
      params.set('status', selectedStatus);
    } else {
      params.delete('status');
    }

    // Don't update URL if there are no parameters
    if (params.toString() === '') {
      if (window.location.search) {
        window.history.pushState(null, '', window.location.pathname);
      }
      return;
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, '', newUrl);
  }, [searchQuery, activeTypeFilter, selectedStatus, isInitialized]);

  return {
    searchQuery,
    setSearchQuery,
    activeTypeFilter,
    setActiveTypeFilter,
    selectedStatus,
    setSelectedStatus,
    isInitialized,
    clearFilters,
  };
};
